/**
 * App entry point. The routing surface — shells, route table, and lazy-loaded
 * page modules — lives under `./routing/` (extracted during iter52 refactor).
 *
 * This file is intentionally tiny: it wires the top-level providers, the global
 * toaster, and the one-shot side effects (referral capture, static OG cleanup,
 * Pro "Made with Emergent" badge removal).
 */
import { useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { Toaster } from "sonner";
import { ConfirmDialogHost } from "./components/ConfirmDialog";
import { FocusTrap } from "./components/FocusTrap";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";

import { AuthProvider } from "./context/AuthContext";
import NativePushBootstrap from "./components/NativePushBootstrap";
import AutoTranslate from "./components/AutoTranslate";
import { ThemeProvider } from "./context/ThemeContext";
import AppRouter from "./routing/AppRouter";

export default function App() {
    useEffect(() => {
        // Capture ?ref=<username> from the URL on first load. We don't strip it from the URL —
        // the user may be on a marketing or landing page. AuthContext drains this on successful
        // /auth/me so it's a one-shot operation per visitor.
        try {
            const params = new URLSearchParams(window.location.search);
            const ref = (params.get("ref") || "").trim().replace(/[^a-zA-Z0-9_]/g, "").slice(0, 32);
            if (ref) {
                localStorage.setItem("hc_pending_ref", ref);
            }
        } catch { /* noop — private browsing etc */ }
        // index.html ships static og:* + twitter:* tags so non-JS social-media crawlers
        // (WhatsApp, iMessage, etc.) get a rich preview when sharing the bare domain.
        // But Facebook/Twitter parsers pick the FIRST og:title — which would beat Helmet's
        // per-route tags. Strip the static ones once React mounts so Helmet's are the only
        // og:*/twitter:* tags in the live DOM. We mark static tags with `data-static-og="1"`
        // in index.html so this strip is precise and can never accidentally remove a Helmet tag.
        try {
            document.querySelectorAll('meta[data-static-og="1"]').forEach((el) => el.remove());
        } catch { /* noop */ }
        // Pro account: hide "Made with Emergent" badge from DOM (in case CSS is bypassed)
        const kill = () => {
            const el = document.getElementById("emergent-badge");
            if (el && el.parentNode) el.parentNode.removeChild(el);
        };
        kill();
        const obs = new MutationObserver(kill);
        obs.observe(document.body, { childList: true, subtree: false });
        return () => obs.disconnect();
    }, []);

    return (
        <div className="App">
            <HelmetProvider>
                <BrowserRouter>
                    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID}>
                        <AuthProvider>
                            <ThemeProvider>
                                <AppRouter />
                                <ConfirmDialogHost />
                                <FocusTrap />
                                <AutoTranslate />
                                <NativePushBootstrap />
                                <Toaster
                                    theme="dark"
                                    position="bottom-right"
                                    style={{ zIndex: 60 }}
                                    toastOptions={{
                                        style: {
                                            background: "#131a2a",
                                            border: "1px solid #1e293b",
                                            color: "#f8fafc",
                                            fontFamily: "IBM Plex Mono, monospace",
                                            fontSize: "12px",
                                            borderRadius: "2px",
                                        },
                                    }}
                                />
                            </ThemeProvider>
                        </AuthProvider>
                    </GoogleOAuthProvider>
                </BrowserRouter>
            </HelmetProvider>
        </div>
    );
}
